import axios from "axios";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import PageTitle from "../../Utils/smsTitle";
import { SubHeader } from "../../../Containers/SubHeader";
import { config } from "../../../Helpers/env";
import { EnhancedSearchTableToolbar } from "../../Utils/Tables/CustomTable";
import SelectInput from "../../Utils/FormInputs/SelectInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUndo, faFileExcel, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import { BackButton } from "../../Utils/Buttons/DataExportationButton";
import { LoadingGif } from "../../Utils/Loader";
import { Container } from "../../Utils/styledComponents";

const BillReversals = () => {
  const storedUser = JSON.parse(sessionStorage.getItem("user") || "{}");
  const userData = storedUser?.token?.[1] || {};
  const roleName =
    typeof userData.role_name === "string"
      ? userData.role_name.trim().toLowerCase()
      : "";
  const hasReportAccess = roleName === "admin" || roleName === "auditor";

  const [reversals, setReversals] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(false);

  const [state, setState] = useState({
    start_date: "",
    end_date: "",
    currency_id: ""
  });
  const [term, setTerm] = useState("");

  const { start_date, end_date, currency_id } = state;

  useEffect(() => {
    if (!hasReportAccess) return;
    fetchCurrencies();
  }, [hasReportAccess]);

  useEffect(() => {
    if (!hasReportAccess) return;
    fetchReversals();
  }, [hasReportAccess, start_date, end_date, term, currency_id]);

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(
        `${config.smsUrl}/accounting/currencies`
      );
      const data = response.data || {};
      const currencyList =
        data.data ||
        data.currencies ||
        (Array.isArray(data) ? data : []);

      const options = currencyList.map((currency) => ({
        value: currency.id,
        text: `${currency.currency_name} (${currency.currency_symbol})`
      }));
      setCurrencies(options);
    } catch (error) {
      console.error("Error fetching currencies:", error);
      toast.error("Failed to fetch currencies.");
    }
  };

  const fetchReversals = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${config.smsUrl}/creport/reversal_refund_report`,
        {
          params: {
            page: 1,
            start_date: start_date || undefined,
            end_date: end_date || undefined,
            search: term || undefined,
            currency_id: currency_id || undefined
          }
        }
      );
      setReversals(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching reversals:", error);
      toast.error("Failed to fetch bill reversals.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event) => {
    const { value, name } = event.target;
    setState({ ...state, [name]: value });
  };

  const handleSearch = (event) => {
    setTerm(event.target.value);
  };

  if (!hasReportAccess) {
    return (
      <>
        <PageTitle title="Access Denied" />
        <SubHeader title="Reports" subTitle="Access Denied" />
        <Container>
          <div className="text-center mt-5">
            <h5>Access Denied</h5>
            <p>
              You do not have permission to view the Bill Reversals & Refunds
              report.
            </p>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <PageTitle title="Bill Reversals & Refunds" />
      <SubHeader title="Reports" subTitle="Bill Reversals & Refunds">
        <FontAwesomeIcon icon={faUndo} />
      </SubHeader>

      <Container className="mt-1">
        {/* ===== TOP SECTION ===== */}
        <div style={{ padding: "8px 0 0 0" }}>
          {/* Back button */}
          <div style={{ marginBottom: "12px" }}>
            <BackButton to="/reports/generalreports" text="Reports" />
          </div>

          {/* Filters – tight */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "10px",
              marginBottom: "14px"
            }}
          >
            <input
              type="date"
              name="start_date"
              value={start_date}
              onChange={handleChange}
              className="form-control form-control-sm"
              style={{
                width: "145px",
                height: "32px",
                padding: "2px 8px",
                fontSize: "13px",
                borderRadius: "6px"
              }}
            />
            <input
              type="date"
              name="end_date"
              value={end_date}
              onChange={handleChange}
              className="form-control form-control-sm"
              style={{
                width: "145px",
                height: "32px",
                padding: "2px 8px",
                fontSize: "13px",
                borderRadius: "6px"
              }}
            />

            <div style={{ minWidth: "180px" }}>
              <SelectInput
                value={currency_id}
                onChange={handleChange}
                name="currency_id"
                options={currencies}
                defaultOption="ALL Currencies"
              />
            </div>
          </div>
        </div>

        {/* Search */}
        <div style={{ marginBottom: "12px" }}>
          <EnhancedSearchTableToolbar
            term={term}
            handler={handleSearch}
            placeholder="Search patient, invoice, visit..."
          />
        </div>

        {/* TABLE */}
        <div
          className="table-responsive bg-white"
          style={{
            borderRadius: "10px",
            overflow: "hidden",
            border: "1px solid #e0e0e0"
          }}
        >
          <table className="table table-hover align-middle mb-0">
            <thead style={{ backgroundColor: "#e8f5f3" }}>
              <tr>
                <th style={{ fontSize: "13px", fontWeight: 600 }}>No.</th>
                <th style={{ fontSize: "13px", fontWeight: 600 }}>Visit ID</th>
                <th style={{ fontSize: "13px", fontWeight: 600 }}>Invoice</th>
                <th style={{ fontSize: "13px", fontWeight: 600 }}>Patient</th>
                <th style={{ fontSize: "13px", fontWeight: 600 }}>
                  Item / Service
                </th>
                <th style={{ fontSize: "13px", fontWeight: 600 }}>Type</th>
                <th style={{ fontSize: "13px", fontWeight: 600 }}>Amount</th>
                <th style={{ fontSize: "13px", fontWeight: 600 }}>Reason</th>
                <th style={{ fontSize: "13px", fontWeight: 600 }}>
                  Processed By
                </th>
                <th style={{ fontSize: "13px", fontWeight: 600 }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="text-center py-4">
                    <LoadingGif />
                  </td>
                </tr>
              ) : reversals.length === 0 ? (
                <tr>
                  <td
                    colSpan="10"
                    className="text-center py-4 text-muted"
                  >
                    No reversals or refunds found.
                  </td>
                </tr>
              ) : (
                reversals.map((row, index) => (
                  <tr key={`${row.bill_id}-${row.action_type}-${index}`}>
                    <td style={{ fontSize: "13px" }}>{index + 1}</td>
                    <td style={{ fontSize: "13px" }}>
                      {row.visit_id || "N/A"}
                    </td>
                    <td style={{ fontSize: "13px" }}>
                      {row.invoice_no || "N/A"}
                    </td>
                    <td style={{ fontSize: "13px" }}>
                      {row.patient_name || "N/A"}
                    </td>
                    <td style={{ fontSize: "13px" }}>
                      {row.item_service || "N/A"}
                    </td>
                    <td style={{ fontSize: "13px" }}>
                      <span
                        style={{
                          background:
                            row.action_type === "Refund"
                              ? "#fff3cd"
                              : "#f8d7da",
                          color:
                            row.action_type === "Refund"
                              ? "#856404"
                              : "#721c24",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: 600
                        }}
                      >
                        {row.action_type || "N/A"}
                      </span>
                    </td>
                    <td style={{ fontSize: "13px", fontWeight: 600 }}>
                      {row.currency_symbol || ""}
                      {row.amount !== null && row.amount !== undefined
                        ? ` ${Number(row.amount).toLocaleString()}`
                        : " N/A"}
                    </td>
                    <td style={{ fontSize: "13px" }}>
                      {row.reason || "N/A"}
                    </td>
                    <td style={{ fontSize: "13px" }}>
                      {row.processed_by || "N/A"}
                    </td>
                    <td style={{ fontSize: "13px" }}>
                      {row.processed_date
                        ? new Date(row.processed_date).toLocaleString()
                        : "N/A"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Container>
    </>
  );
};

export default BillReversals;